'use client';

import {
  GetPaginatedWellniteArtGalleryResponse,
  WellniteArtImage,
} from '@website/types/art.types';
import React, {useEffect, useRef} from 'react';
import {useTranslations} from 'next-intl';
import {ImageWellniteArt} from '@website/components/ImageWellniteArt';
import {WellniteArtDetails} from '@website/components/Gallery/WellniteArtDetail';
import InfiniteScroll from 'react-infinite-scroll-component';
import {LoadingSpinner} from '@website/components/shared/basics/Loaders';
import './gallery.css';
import {SearchForm} from '@website/components/Gallery/SearchForm';
import {MAX_RESULTS_PER_PAGE} from '@website/components/Gallery/utils';
import Masonry from 'react-masonry-css';

interface GalleryProps {
  images: WellniteArtImage[];
  hasMore: boolean;
  totalPages: number;
  baseUrl: string;
}

const breakpointColumns = {
  default: 5,
  901: 5,
  701: 4,
  501: 3,
  500: 3,
};

export function GalleryGrid({
  images: initialImages,
  hasMore: initialHasMore,
  baseUrl,
}: GalleryProps): React.ReactElement {
  const t = useTranslations('Gallery');
  const [image, setImage] = React.useState<WellniteArtImage | null>(null);

  const [currentPage, setCurrentPage] = React.useState(2);
  const [hasMore, setHasMore] = React.useState(initialHasMore);
  const [images, setImages] = React.useState<WellniteArtImage[]>(initialImages);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedConditions, setSelectedConditions] = React.useState<string[]>(
    [],
  );
  const requestCounter = useRef(0);

  const triggerScroll = () => {
    window.scrollTo({
      top: 0,
    });
    window.scrollTo({
      top: 1,
    });
  };

  useEffect(() => {
    triggerScroll();
  }, []);

  const fetchGallery = async (page: number, limit: number) => {
    const res = await fetch(
      `${baseUrl}/api/wellnite/get-paginated-magic-art-gallery?maxResults=${limit}&page=${page}`,
    );
    const data: GetPaginatedWellniteArtGalleryResponse = await res.json();
    return data.message;
  };

  const searchGallery = async (
    search: string,
    conditions: string[],
    page: number,
    limit: number,
  ) => {
    const res = await fetch(
      `${baseUrl}/api/wellnite/search-magic-art-gallery?searchTerm=${search}&maxResults=${limit}&page=${page}&selectedConditionTags=${conditions.join(',')}&galleryType=wellnite_collection`,
    );
    const data: GetPaginatedWellniteArtGalleryResponse = await res.json();
    return data.message;
  };

  const handleImageClick = (article: WellniteArtImage) => {
    setImage(article);
    window.history.pushState({}, '', `/gallery/${article._id}`);
  };

  const loadImages = async (submit?: {
    searchTerm?: string;
    conditions?: string[];
    page?: number;
  }) => {
    const requestId = ++requestCounter.current;
    const page = submit?.page ?? currentPage;

    if (submit || searchTerm || selectedConditions.length) {
      const data = await searchGallery(
        submit?.searchTerm ?? searchTerm,
        submit?.conditions ?? selectedConditions,
        page,
        MAX_RESULTS_PER_PAGE,
      );
      // if multiple loadImages are being called due to fast clicking ensure only latest results are used
      if (requestId !== requestCounter.current) {
        return;
      }
      setImages(images =>
        page === 1 ? data.results : [...images, ...data.results],
      );
      setHasMore(data.hasMore);
      setCurrentPage(page + 1);
      return;
    }

    const data = await fetchGallery(page, MAX_RESULTS_PER_PAGE);
    // if multiple loadImages are being called due to fast clicking ensure only latest results are used
    if (requestId !== requestCounter.current) {
      return;
    }

    setImages(images =>
      page === 1 ? data.results : [...images, ...data.results],
    );
    setHasMore(data.hasMore);
    setCurrentPage(page + 1);
  };

  const onSearch = async (search: string, conditions: string[]) => {
    setImages([]);
    setSearchTerm(search);
    setSelectedConditions(conditions);
    setCurrentPage(1);
    setHasMore(true);
    triggerScroll();
    loadImages({searchTerm, conditions, page: 1});
  };

  const config = {
    dataLength: images.length,
    hasMore: hasMore,
    scrollThreshold: -1000,
    initialScrollY: 0,
    next: () => loadImages(),
  };

  return (
    <>
      <section
        aria-label={t('sectionAriaLabel')}
        role="region"
        itemScope
        itemType="http://schema.org/CreativeWork"
      >
        <SearchForm baseUrl={baseUrl} onFormSubmit={onSearch} />
        <InfiniteScroll
          {...config}
          loader={
            <div
              className="w-full flex justify-center items-center mx-auto space-x-3 my-8"
              style={{columnSpan: 'all'}}
            >
              <LoadingSpinner type="Hearts" height={50} displayDelay={0} />
            </div>
          }
          endMessage={
            images?.length !== 0 ? (
              <p
                className="bg-[#ffcb58]/[.15] text-sm rounded-xl p-5 text-center mt-4 text-gray-600 w-full"
                style={{columnSpan: 'all'}}
              >
                {t('noMoreArtwork')}
              </p>
            ) : (
              <p
                className="bg-[#ffcb58]/[.15] text-sm rounded-xl p-5 text-center mt-4 text-gray-600 w-full"
                style={{columnSpan: 'all'}}
              >
                {t('noArtFound')}
              </p>
            )
          }
          className="xs:-mx-6 w-[1280px] mx-auto max-width-[100%] gallery"
        >
          <Masonry
            breakpointCols={breakpointColumns}
            className="gallery-masonry-grid"
            columnClassName="gallery-masonry-grid_column"
          >
            {images.map(image => (
              <div
                key={image._id}
                onClick={() => handleImageClick(image)}
                className="block group mb-6 cursor-pointer"
                aria-label={`Mindful artwork`}
                title={`Mindful artwork`}
                itemProp="url"
              >
                <ImageWellniteArt
                  className={
                    'sm:rounded-[40px] rounded-[15px] hover:scale-105 group-hover:scale-105 transition-transform duration-300 object-cover'
                  }
                  image={image}
                />
              </div>
            ))}
          </Masonry>
        </InfiniteScroll>
      </section>
      {image ? (
        <WellniteArtDetails
          image={image}
          onClose={() => setImage(null)}
        ></WellniteArtDetails>
      ) : null}
    </>
  );
}
